import { useState, useRef, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { TabPanel, TabPanels, Tabs } from '@reach/tabs';
import intl from 'react-intl-universal';
import { Tab, TabList, Button } from '@getsynapse/design-system';
import PageTitle from 'Molecules/PageTitle/PageTitle';
import {
  isLDTeamManager,
  isUserFacilitator,
  isUserOrganizationAdmin,
} from 'state/User/userSlice';
import { SCHEDULE_TABS } from 'utils/constants/learningSchedule';
import Events from './components/Events/Events';
import Facilitators from './components/Facilitators/Facilitators';
import { EventsScheduleRef } from './components/ScheduleCalendar/ScheduleCalendar';
import MySchedule from './components/MySchedule/MySchedule';
import { useFlags } from 'launchdarkly-react-client-sdk';
const LearningSchedule = () => {
  const isFacilitator = useSelector(isUserFacilitator);
  const isAdmin = useSelector(isUserOrganizationAdmin);
  const isTeamManager = useSelector(isLDTeamManager);

  const { fmFacilitatorCreateEvents } = useFlags();
  const SCHEDULE_TABS_MAPPING: { [key: string]: number | undefined } = useMemo(
    () =>
      isFacilitator
        ? {
            [SCHEDULE_TABS.MY_SCHEDULE]: 0,
            [SCHEDULE_TABS.EVENTS]: 1,
            [SCHEDULE_TABS.FACILITATORS]: 2,
          }
        : {
            [SCHEDULE_TABS.EVENTS]: 0,
            [SCHEDULE_TABS.FACILITATORS]: 1,
            [SCHEDULE_TABS.MY_SCHEDULE]: undefined,
          },
    [isFacilitator]
  );

  const queryParams = new URLSearchParams(window.location.search);
  const activeTab = queryParams.get('activeTab');

  const [currentTabIndex, setCurrentTabIndex] = useState(
    activeTab ? SCHEDULE_TABS_MAPPING[activeTab] : 0
  );

  const pageToLoad = useMemo(() => {
    if (
      isFacilitator &&
      !fmFacilitatorCreateEvents &&
      !isAdmin &&
      !isTeamManager
    ) {
      return SCHEDULE_TABS.MY_SCHEDULE;
    }

    if ((isAdmin || isTeamManager) && !isFacilitator) {
      return currentTabIndex === 0
        ? SCHEDULE_TABS.EVENTS
        : SCHEDULE_TABS.FACILITATORS;
    }

    if (
      ((isAdmin || isTeamManager) && isFacilitator) ||
      (isFacilitator && fmFacilitatorCreateEvents)
    ) {
      switch (currentTabIndex) {
        case 0:
          return SCHEDULE_TABS.MY_SCHEDULE;
        case 1:
          return SCHEDULE_TABS.EVENTS;
        case 2:
          return SCHEDULE_TABS.FACILITATORS;
      }
    }
  }, [
    currentTabIndex,
    isFacilitator,
    isAdmin,
    isTeamManager,
    fmFacilitatorCreateEvents,
  ]);

  const eventsScheduleRef = useRef<EventsScheduleRef>(null);
  const onAddingEvent = () => {
    eventsScheduleRef?.current?.onAddingEvent();
  };

  const handleChangeTab = (index: number) => {
    if (index === currentTabIndex) return;
    setCurrentTabIndex(index);
    window.history.replaceState(
      {},
      '',
      `${window.location.pathname}?activeTab=${
        Object.keys(SCHEDULE_TABS_MAPPING)[index]
      }`
    );
  };

  return (
    <>
      <PageTitle titleComponent={intl.get('SCHEDULE.TITLE')} />
      {(isAdmin || isTeamManager || isFacilitator) && (
        <Tabs
          index={currentTabIndex}
          onChange={handleChangeTab}
          className='mx-6'
        >
          {({ selectedIndex }) => (
            <>
              <div className='flex justify-between'>
                {(isAdmin ||
                  isTeamManager ||
                  (isFacilitator && fmFacilitatorCreateEvents)) && (
                  <TabList className='w-5/12 mb-4'>
                    {isFacilitator && (
                      <Tab index={0}>
                        {intl.get('SCHEDULE.TABS.MY_SCHEDULE')}
                      </Tab>
                    )}
                    {(isAdmin ||
                      isTeamManager ||
                      (isFacilitator && fmFacilitatorCreateEvents)) && (
                      <>
                        <Tab index={isFacilitator ? 1 : 0}>
                          {intl.get('SCHEDULE.TABS.EVENTS')}
                        </Tab>
                        <Tab index={isFacilitator ? 2 : 1}>
                          {intl.get('SCHEDULE.TABS.FACILITATORS')}
                        </Tab>
                      </>
                    )}
                  </TabList>
                )}
                {(isAdmin ||
                  isTeamManager ||
                  (isFacilitator && fmFacilitatorCreateEvents)) && (
                  <Button
                    className='h-8'
                    data-testid='add-event_button'
                    onClick={onAddingEvent}
                  >
                    {intl.get('SCHEDULE.ADD_EVENT_BUTTON')}
                  </Button>
                )}
              </div>

              <TabPanels>
                {isFacilitator && ( // this check is to only show myschedule for users who are facilitators
                  <TabPanel key={0}>
                    {pageToLoad === SCHEDULE_TABS.MY_SCHEDULE && (
                      <MySchedule
                        pageToLoad={pageToLoad}
                        ref={eventsScheduleRef}
                      />
                    )}
                  </TabPanel>
                )}
                {(isAdmin ||
                  isTeamManager ||
                  (isFacilitator && fmFacilitatorCreateEvents)) && (
                  <>
                    <TabPanel key={isFacilitator ? 1 : 0}>
                      {pageToLoad === SCHEDULE_TABS.EVENTS && (
                        <Events
                          ref={eventsScheduleRef}
                          pageToLoad={pageToLoad}
                        />
                      )}
                    </TabPanel>
                    <TabPanel key={isFacilitator ? 2 : 1}>
                      {pageToLoad === SCHEDULE_TABS.FACILITATORS && (
                        <Facilitators
                          ref={eventsScheduleRef}
                          pageToLoad={pageToLoad}
                        />
                      )}
                    </TabPanel>
                  </>
                )}
              </TabPanels>
            </>
          )}
        </Tabs>
      )}
    </>
  );
};

export default LearningSchedule;
